import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../../core/service/language.service';
import { MessageService } from '../../../core/service/message.service';
import { LettingFeature } from '../../model/letting-feature';
import { LettingFeatureService } from '../../service/data/letting-feature.service';

interface LettingFeatureDeletionGroup {
  feature: LettingFeature;
  relationIds: string[];
  control: FormControlEx<boolean>;
}

@Component({
    selector: 'app-set-letting-features-form',
    templateUrl: './set-letting-features-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SetLettingFeaturesFormComponent implements OnInit, OnDestroy {
  constructor(
    private readonly _service: LettingFeatureService,
    private readonly _dialogService: ActionWindowService,
    private readonly _messageService: MessageService,
    private readonly _languageService: LanguageService,
    private readonly _fb: FormBuilder,
    private readonly _fcf: FormControlExFactory
  ) {}

  @Input() dataContext: { filter: any; includedIds: string[]; excludedIds: string[]; affectedItemsCount: number };

  addIncluded: FormArray;
  addNotIncluded: FormArray;
  deleteIncluded: LettingFeatureDeletionGroup[];
  deleteNotIncluded: LettingFeatureDeletionGroup[];
  initialized = false;

  private readonly _sub = new Subscription();
  private readonly _selectionItemsByControl = new Map<AbstractControl, SelectionItem<string>[]>();
  private _availableFeatures: LettingFeature[] = [];
  private _working = false;

  ngOnInit() {
    this.addIncluded = this._createFeaturesForm('RENTAL_OBJECT.LETTING_FEATURES.FEATURE');
    this.addNotIncluded = this._createFeaturesForm('RENTAL_OBJECT.LETTING_FEATURES.EXCLUDED_FEATURE');

    this._sub.add(
      forkJoin({
        usedFeatures: this._service.getUsedFeaturesByFilter(
          this.dataContext.filter,
          this.dataContext.includedIds,
          this.dataContext.excludedIds,
          this.dataContext.affectedItemsCount
        ),
        availableFeatures: this._service.getAvailableForRentalObjects(),
      }).subscribe(({ usedFeatures, availableFeatures }) => {
        this._availableFeatures = availableFeatures;
        this._selectionItemsByControl.clear();
        this.deleteIncluded = this._createDeletionGroups(usedFeatures.usedIncludedFeatures);
        this.deleteNotIncluded = this._createDeletionGroups(usedFeatures.usedNotIncludedFeatures);
        this.initialized = true;
      })
    );
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  cancel() {
    this._dialogService.close();
  }

  getFeatureSelectionItems(control: AbstractControl): SelectionItem<string>[] {
    const existingItems = this._selectionItemsByControl.get(control);
    if (existingItems) {
      return existingItems;
    }

    const items = this._availableFeatures.map(feature => {
      const caption = this._getFeatureCaption(feature);
      const name = feature.isActive ? caption : `${caption} (${this._languageService.instant('LETTING_FEATURE.INACTIVE')})`;
      const isDisabled = () => !feature.isActive || this._isSelectedByAnotherAddControl(feature.id, control);
      return {
        name,
        value: feature.id,
        get disabled() {
          return isDisabled();
        },
      } as SelectionItem<string>;
    });

    this._selectionItemsByControl.set(control, items);
    return items;
  }

  private _getDeletionCaption(feature: LettingFeature, relationCount: number): string {
    const caption = this._getFeatureCaption(feature);
    const inactiveSuffix = feature.isActive ? '' : ` (${this._languageService.instant('LETTING_FEATURE.INACTIVE')})`;
    const affectedObjects = this._languageService.instant('RENTAL_OBJECT.LETTING_FEATURES.AFFECTED_OBJECTS', {
      count: relationCount,
      total: this.dataContext.affectedItemsCount,
    });
    return `${caption}${inactiveSuffix} (${affectedObjects})`;
  }

  submit() {
    if (this._working || !this.initialized) return;

    this._working = true;
    const model = {
      removedFeatures: [
        ...this.deleteIncluded.filter(group => group.control.value).flatMap(group => group.relationIds),
        ...this.deleteNotIncluded.filter(group => group.control.value).flatMap(group => group.relationIds),
      ],
      addedIncludedLettingFeatureIds: this._getSelectedFeatureIds(this.addIncluded),
      addedNotIncludedLettingFeatureIds: this._getSelectedFeatureIds(this.addNotIncluded),
      filteredActionModel: {
        filter: this.dataContext.filter,
        includedIds: this.dataContext.includedIds,
        excludedIds: this.dataContext.excludedIds,
        affectedItemsCount: this.dataContext.affectedItemsCount,
      },
    };

    this._sub.add(
      this._service.updateRentalObjectFeaturesByFilter(model).subscribe({
        next: res => {
          this._messageService.showSuccess();
          this._dialogService.complete(res);
        },
        error: () => {
          this._working = false;
        },
      })
    );
  }

  private _createDeletionGroups(items: LettingFeature[]): LettingFeatureDeletionGroup[] {
    const groups = new Map<string, Pick<LettingFeatureDeletionGroup, 'feature' | 'relationIds'>>();

    items.forEach(feature => {
      const featureId = feature.lettingFeatureId ?? feature.id;
      let group = groups.get(featureId);
      if (!group) {
        group = {
          feature,
          relationIds: [],
        };
        groups.set(featureId, group);
      }
      group.relationIds.push(feature.id);
    });

    return Array.from(groups.values()).map(group => ({
      ...group,
      control: this._fcf.create(this._getDeletionCaption(group.feature, group.relationIds.length), false),
    }));
  }

  private _createFeaturesForm(controlHeader: string): FormArray {
    const form = this._fb.array([]);
    this._appendFeatureControl(form, controlHeader);
    return form;
  }

  private _appendFeatureControl(form: FormArray, controlHeader: string): void {
    const control = this._fcf.create(controlHeader, null);
    this._sub.add(control.valueChanges.subscribe(() => this._checkFeatures(form, controlHeader)));
    form.push(control);
  }

  private _checkFeatures(form: FormArray, controlHeader: string): void {
    form.markAsDirty();

    for (let index = form.controls.length - 1; index >= 0; index--) {
      const control = form.controls[index];
      if (!control.value) {
        this._selectionItemsByControl.delete(control);
        form.removeAt(index);
      }
    }

    this._appendFeatureControl(form, controlHeader);
  }

  private _getSelectedFeatureIds(form: FormArray): string[] {
    return form.controls.map(control => control.value).filter(value => !!value);
  }

  private _isSelectedByAnotherAddControl(lettingFeatureId: string, currentControl: AbstractControl): boolean {
    return [...this.addIncluded.controls, ...this.addNotIncluded.controls].some(
      control => control !== currentControl && control.value === lettingFeatureId
    );
  }

  private _getFeatureCaption(feature: LettingFeature): string {
    return feature.phrase.find(x => x.cultureName === this._languageService.currentCultureLang)?.translationText ?? feature.phrase.find(x => !!x.translationText)?.translationText ?? '';
  }
}
